import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { ErrorContext } from '../context/ErrorContext';
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';
import ComparisonFilters from './ComparisonFilters';
import ComparisonTable from './ComparisonTable';
import HasPermission from '../utils/HasPermission';
import './css/ComparisonPage.css';
import { PageShell, SectionCard } from "../components/ui";

const apiUrl = API_URL;

const ComparisonPage = () => {
  const axios = axiosInstance;
  const { user } = useContext(AuthContext);
  const { showError } = useContext(ErrorContext);
  const navigate = useNavigate();

  const [nations, setNations] = useState([]);
  const [alliances, setAlliances] = useState([]);
  const [commanders, setCommanders] = useState([]);
  const [selectedNations, setSelectedNations] = useState([]);
  const [selectedAlliances, setSelectedAlliances] = useState([]);
  const [selectedCommanders, setSelectedCommanders] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingScopedOptions, setLoadingScopedOptions] = useState(false);
  const [applying, setApplying] = useState(false);

  const canCompare = HasPermission('readCompare');
  const canAccessComparePage = canCompare && (user?.role === 'superUser' || user?.role === 'supporter');

  useEffect(() => {
    if (user && !canAccessComparePage) {
      navigate('/home');
    }
  }, [user, canAccessComparePage, navigate]);

  useEffect(() => {
    if (!canAccessComparePage) return;

    let isMounted = true;
    const loadFilters = async () => {
      setLoadingOptions(true);
      try {
        const nationsResponse = await axios.get(`${apiUrl}/nations`);

        if (!isMounted) return;

        const sortedNations = [...nationsResponse.data].sort(
          (a, b) => (a.number ?? 0) - (b.number ?? 0)
        );
        setNations(sortedNations);
      } catch (error) {
        console.error('Error loading filter options:', error);
        showError(error);
      } finally {
        if (isMounted) {
          setLoadingOptions(false);
        }
      }
    };

    loadFilters();
    return () => {
      isMounted = false;
    };
  }, [axios, canAccessComparePage, showError]);

  const selectedNationIds = useMemo(
    () => selectedNations.map((nation) => nation._id).filter(Boolean),
    [selectedNations]
  );
  const selectedNationIdSet = useMemo(
    () => new Set(selectedNationIds),
    [selectedNationIds]
  );
  const selectedAllianceIds = useMemo(
    () => selectedAlliances
      .filter((alliance) => {
        const allianceNation =
          typeof alliance.nation === 'string'
            ? alliance.nation
            : alliance.nation?._id;
        return selectedNationIdSet.has(allianceNation);
      })
      .map((alliance) => alliance._id)
      .filter(Boolean),
    [selectedAlliances, selectedNationIdSet]
  );
  const selectedNationKey = selectedNationIds.join(',');
  const selectedAllianceKey = selectedAllianceIds.join(',');

  useEffect(() => {
    if (!selectedNations.length) {
      setAlliances([]);
      setCommanders([]);
      setSelectedAlliances([]);
      setSelectedCommanders([]);
    }
  }, [selectedNations]);

  useEffect(() => {
    if (!canAccessComparePage || !selectedNationIds.length) return;

    let isMounted = true;
    const loadScopedFilters = async () => {
      setLoadingScopedOptions(true);
      try {
        const response = await axios.get(`${apiUrl}/nations/compare/filter-options`, {
          params: {
            nationIds: selectedNationIds,
            ...(selectedAllianceIds.length ? { allianceIds: selectedAllianceIds } : {})
          }
        });

        if (!isMounted) return;

        const nextAlliances = Array.isArray(response.data?.alliances)
          ? response.data.alliances
          : [];
        const nextCommanders = Array.isArray(response.data?.commanders)
          ? response.data.commanders
          : [];
        const nextAllianceIds = new Set(nextAlliances.map((alliance) => alliance._id));
        const nextCommanderIds = new Set(nextCommanders.map((commander) => commander._id));

        setAlliances(nextAlliances);
        setCommanders(nextCommanders);
        setSelectedAlliances((current) => {
          const filtered = current.filter((alliance) => nextAllianceIds.has(alliance._id));
          return filtered.length === current.length ? current : filtered;
        });
        setSelectedCommanders((current) => {
          const filtered = current.filter((commander) => nextCommanderIds.has(commander._id));
          return filtered.length === current.length ? current : filtered;
        });
      } catch (error) {
        console.error('Error loading scoped comparison filter options:', error);
        showError(error);
      } finally {
        if (isMounted) {
          setLoadingScopedOptions(false);
        }
      }
    };

    loadScopedFilters();
    return () => {
      isMounted = false;
    };
  }, [
    axios,
    canAccessComparePage,
    selectedNationKey,
    selectedAllianceKey,
    selectedNationIds,
    selectedAllianceIds,
    showError
  ]);

  const handleApply = async () => {
    if (!selectedNations.length) return;

    const nationIds = selectedNations.map((nation) => nation._id);
    const allianceIds = selectedAllianceIds;
    const commanderIds = selectedCommanders
      .filter((commander) => {
        const commanderNation =
          typeof commander.nation === 'string'
            ? commander.nation
            : commander.nation?._id;
        return selectedNationIdSet.has(commanderNation);
      })
      .map((commander) => commander._id);

    setApplying(true);
    try {
      const response = await axios.get(`${apiUrl}/nations/compare`, {
        params: {
          nationIds,
          ...(allianceIds.length ? { allianceIds } : {}),
          ...(commanderIds.length ? { commanderIds } : {})
        }
      });
      setComparisonData(response.data);
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      showError(error);
    } finally {
      setApplying(false);
    }
  };

  if (!canAccessComparePage) {
    return null;
  }

  return (
    <PageShell maxWidth={false} containerSx={{ px: { xs: 1, md: 2 }, maxWidth: "none" }}>
      <div className="comparison-page">
        <SectionCard sx={{ p: 1.5 }}>
          <ComparisonFilters
            nations={nations}
            alliances={alliances}
            commanders={commanders}
            selectedNations={selectedNations}
            onNationsChange={setSelectedNations}
            selectedAlliances={selectedAlliances}
            onAlliancesChange={setSelectedAlliances}
            selectedCommanders={selectedCommanders}
            onCommandersChange={setSelectedCommanders}
            onApply={handleApply}
            loadingOptions={loadingOptions}
            loadingScopedOptions={loadingScopedOptions}
            applying={applying}
          />
        </SectionCard>

        {comparisonData && (
          <ComparisonTable comparisonData={comparisonData} />
        )}
      </div>
    </PageShell>
  );
};

export default ComparisonPage;
